<template>
  <vxe-list
      :size="vxeSize"
      :loading="loading || listLoading"
      :data="list"
      :height="height"
      className="rock-tree"
      :class="{ 'rock-tree__auto-height': !height }"
  >
    <template #default="{ items }">
      <template v-if="list.length && !loading && !listLoading">
        <template v-for="item in items">
          <div
              :key="item[props.value || 'id']"
              class="rock-tree-item"
              :class="[
              `level-${item._LEVEL}`,
              {
                'has-child': item._HAS_CHILDREN,
                'is-expand': item._EXPAND,
                'is-current':
                  !showCheckbox &&
                  !isNull(currentKey) &&
                  item[props.value || 'id'] === currentKey,
              },
            ]"
              :style="{
              paddingLeft: `${item._LEVEL * indent}px`,
              height: `${rowHeight}px`,
              lineHeight: `${rowHeight}px`,
            }"
          >
            <i
                :style="{
                padding: `${rowHeight / 2 - 6}px`,
              }"
                class="tree-icon vxe-icon-caret-right"
                @click="toggleTreeNode(item)"
            />
            <vxe-checkbox
                v-if="
                showCheckbox &&
                (disableBranchNodes
                  ? !item._HAS_CHILDREN &&
                    (lastLevelKey && !isNull(lastLevelValue)
                      ? item[lastLevelKey] === lastLevelValue
                      : true)
                  : true)
              "
                v-model="item._CHECKED"
                :indeterminate="item._INDETERMINATE"
                :title="item[props.label || 'name']"
                @change="handleChecked(item)"
            >
              {{ item[props.label || "name"] }}
            </vxe-checkbox>
            <div
                v-else
                :title="item[props.label || 'name']"
                :style="{
                width: `calc(100% - ${rowHeight}px)`,
              }"
                class="tree-label"
                @click="handleNodeClick(item)"
            >
              {{ item[props.label || "name"] }}
            </div>
            <slot name="node" :node="item"/>
          </div>
        </template>
      </template>
      <div v-else-if="!loading && !listLoading" class="el-tree__empty-block">
        <span class="el-tree__empty-text">{{ emptyText }}</span>
      </div>
    </template>
  </vxe-list>
</template>

<script>
import {mapGetters} from "vuex";
import {isNull} from "@/utils/tools";

export default {
  name: "RockTree",
  install(Vue) {
    Vue.component("RockTree", this);
  },
  props: {
    data: Array,
    emptyText: {
      type: String,
      default: "暂无数据",
    },
    props: {
      default() {
        return {
          value: "id",
          children: "children",
          label: "name",
          disabled: "disabled",
        };
      },
    },
    showCheckbox: Boolean,
    height: [Number, String],
    loading: Boolean,
    autoResize: Boolean, // 自动监听父元素的变化去重新计算列表（对于父元素可能存在动态变化的场景可能会用到）
    rowHeight: {
      type: Number,
      default: 34,
    },
    filter: String,
    defaultExpandAll: Boolean,
    // 加载时应自动扩展多少级分支节点；0为默认使所有分支节点扩展。
    defaultExpandLevel: {
      type: Number,
      default: 0,
    },
    currentKey: [String, Number],
    defaultCheckedKeys: Array,
    highlightCurrent: Boolean,
    indent: {
      type: Number,
      default: 18,
    },
    // 是否阻止选择分支节点
    disableBranchNodes: Boolean,
    lastLevelKey: String,
    lastLevelValue: [String, Number],
  },
  data() {
    return {
      isNull,
      listLoading: false,
      list: [],
      fullTree: [],
    };
  },
  computed: {
    ...mapGetters({
      vxeSize: "vxeSize",
    }),
    // 解决数组监听新旧值相同
    convertedDefaultCheckedKeys() {
      if (this.defaultCheckedKeys && this.defaultCheckedKeys[0]) {
        return JSON.parse(JSON.stringify(this.defaultCheckedKeys));
      } else {
        return this.defaultCheckedKeys;
      }
    },
  },
  watch: {
    data: {
      handler(newVal) {
        if (newVal && newVal[0]) this.setData(newVal);
        else {
          this.tree = [];
          this.fullTree = [];
          this.list = [];
        }
      },
      immediate: true,
    },
    filter() {
      this.handleFilter();
    },
    convertedDefaultCheckedKeys(newVal, oldVal) {
      if (newVal !== oldVal && this.showCheckbox)
        this._initDefaultCheckedNodes();
    },
  },
  methods: {
    setData(newVal) {
      this.listLoading = true;
      const hasRoot = this.XEUtils.find(newVal, (item) => item[this.props.value || "id"] === "0")
      const data = this.XEUtils.map(this._.cloneDeep(newVal), (item) => ({
        ...item,
        parentId: item.parentId === "0" && !hasRoot ? "" : item.parentId,
      }));
      const treeData = this.XEUtils.toArrayTree(data, {
        strict: true,
        key: this.props.value || "id",
      });
      // 将树结构拍平，构建列表树结构
      this.XEUtils.eachTree(
          treeData,
          (item, index, items, paths, parent, nodes) => {
            item._LEVEL = nodes.length - 1;
            item._HAS_CHILDREN =
                Object.prototype.hasOwnProperty.call(item, "children") &&
                item.children.length > 0;
            item._IS_LEAF = !item._HAS_CHILDREN;
            item._CHECKED = false;
            item._INDETERMINATE = false;
            if (this.defaultExpandAll || this.defaultExpandLevel === 0) {
              item._EXPAND = item._HAS_CHILDREN;
              item._VISIBLE = true; // 是否可见
            } else if (this.defaultExpandLevel === 1) {
              item._EXPAND = false;
              item._VISIBLE = !item._LEVEL; // 是否可见
            } else {
              item._EXPAND = item._LEVEL < this.defaultExpandLevel - 1;
              item._VISIBLE = item._LEVEL < this.defaultExpandLevel; // 是否可见
            }
          }
      );
      this.tree = treeData;
      this.refreshTree();
      this.listLoading = false;
    },

    toggleTreeNode(row) {
      if (row._HAS_CHILDREN) {
        this.setTreeExpand(row, !row._EXPAND);
      }
    },

    setTreeExpand(row, isExpand) {
      const matchObj = this.XEUtils.findTree(this.tree, (item) => item === row);
      row._EXPAND = isExpand;
      if (matchObj) {
        this.XEUtils.eachTree(
            matchObj.item.children,
            (item, index, items, path, parent) => {
              item._VISIBLE = parent
                  ? parent._EXPAND && parent._VISIBLE
                  : isExpand;
            }
        );
      }
      this.refreshTree();
    },

    allTreeExpand(isExpand) {
      if (isExpand) {
        this.XEUtils.eachTree(this.tree, (item) => {
          item._EXPAND = item._HAS_CHILDREN;
          item._VISIBLE = true;
        });
      } else {
        this.XEUtils.eachTree(this.tree, (item) => {
          item._EXPAND = false;
          item._VISIBLE = !item._LEVEL;
        });
      }
      this.refreshTree();
    },

    // 当前选中节点变化时触发的事件
    handleNodeClick(node) {
      this.toggleTreeNode(node);
      if (!this.showCheckbox) this.$emit("node-click", node);
    },

    _initDefaultCheckedNodes() {
      this.listLoading = true;
      if (this.list && this.list[0]) {
        this.XEUtils.eachTree(this.tree, (item) => {
          item._CHECKED = false;
          item._INDETERMINATE = false;
        });
        this.XEUtils.eachTree(this.tree, (item) => {
          if (
              this.defaultCheckedKeys.includes(item[this.props.value || "id"])
          ) {
            item._CHECKED = true;
            this._setCheckedAndIndeterminate(item);
          }
        });
        this.refreshTree();
      }
      this.listLoading = false;
    },

    _setCheckedAndIndeterminate(node) {
      // 处理子级节点状态
      const _setChecked = (node) => {
        node._INDETERMINATE = false;
        if (node._HAS_CHILDREN) {
          node.children.forEach((child) => {
            child._CHECKED = node._CHECKED;
            _setChecked(child);
          });
        }
      };
      _setChecked(node);

      // 处理父级节点状态
      const _setIndeterminate = () => {
        const matchObj = this.XEUtils.findTree(
            this.filter ? this.fullTree : this.tree,
            (item) => item === node
        );
        if (matchObj && matchObj.nodes.length > 1) {
          for (let i = matchObj.nodes.length - 2; i >= 0; i--) {
            const item = matchObj.nodes[i];
            const checkedCount = item.children.filter(
                (child) => child._CHECKED === true
            ).length;
            const indeterminateCount = item.children.filter(
                (child) => child._INDETERMINATE === true
            ).length;
            item._CHECKED = checkedCount === item.children.length;
            item._INDETERMINATE =
                (checkedCount > 0 && checkedCount < item.children.length) ||
                (checkedCount === 0 && indeterminateCount > 0);
          }
        }
      };
      _setIndeterminate();
    },

    handleChecked(node) {
      this._setCheckedAndIndeterminate(node);
      this.refreshTree();

      // check, 当复选框被点击的时候触发, 共两个参数，依次为：传递给 data 属性的数组中该节点所对应的对象、树目前的选中状态对象，包含 checkedNodes、checkedKeys、halfCheckedNodes、halfCheckedKeys 四个属性
      const checkedNodes = this.XEUtils.filterTree(
          this.filter ? this.fullTree : this.tree,
          (item) => item._INDETERMINATE === false && item._CHECKED === true
      );
      const halfCheckedNodes = this.XEUtils.filterTree(
          this.filter ? this.fullTree : this.tree,
          (item) => item._CHECKED === false && item._INDETERMINATE === true
      );
      this.$emit("check", node, {
        checkedNodes,
        checkedKeys: checkedNodes.map((item) => item[this.props.value || "id"]),
        halfCheckedNodes,
        halfCheckedKeys: halfCheckedNodes.map(
            (item) => item[this.props.value || "id"]
        ),
      });
    },

    handleFilter() {
      if (this.filter) {
        this.tree = this.XEUtils.searchTree(this.fullTree, (item) =>
            item[this.props.label || "name"].includes(this.filter)
        );
      } else this.tree = this._.cloneDeep(this.fullTree);

      if (this.defaultCheckedKeys && this.defaultCheckedKeys[0])
        this._initDefaultCheckedNodes();
      else this.refreshTree();
    },

    refreshTree() {
      const treeList = this.XEUtils.toTreeArray(this.tree);
      this.list = treeList.filter((item) => item._VISIBLE);
      // 处理半选和全选
      if (this.filter) {
        this.XEUtils.eachTree(this.fullTree, (item) => {
          const correspondingNode = this.XEUtils.find(
              this.list,
              (x) =>
                  x[this.props.value || "id"] === item[this.props.value || "id"]
          );
          if (correspondingNode) {
            item._CHECKED = correspondingNode._CHECKED;
            item._INDETERMINATE = correspondingNode._INDETERMINATE;
          }
        });
      } else this.fullTree = this._.cloneDeep(this.tree);
      this.$emit("refresh");
    },
  },
};
</script>

<style scoped lang="scss">
.rock-tree {
  .rock-tree-item {
    display: flex;
    align-content: center;

    .tree-icon {
      color: #c0c4cc;
      cursor: pointer;
      font-size: 12px;
      visibility: hidden;
      user-select: none;
    }

    .tree-label {
      display: inline-block;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      color: #606266;
      font-size: 14px;
      cursor: pointer;
    }

    &:hover {
      background-color: #f5f7fa;

      .tree-label {
        color: $base-color-primary;
      }
    }

    .vxe-checkbox {
      display: flex;
      align-items: center;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .is-current {
    background-color: #f5f7fa;

    .tree-label {
      color: $base-color-primary;
      font-weight: bold;
    }
  }

  .rock-tree__empty {
    display: flex;
    justify-content: center;
    align-items: center;
    color: #999;
    font-size: 14px;
  }
}

.rock-tree .rock-tree-item.has-child .tree-icon {
  visibility: visible;
  transition: all 0.3s;
}

.rock-tree .rock-tree-item.is-expand .tree-icon {
  transform: rotate(90deg);
}

.rock-tree__auto-height {
  height: 100%;

  ::v-deep .vxe-list--virtual-wrapper {
    height: 100%;
  }
}
</style>
